import './NewsListPage.scss';
import { SimpleNumberCard, Checkbox, Button, Icon } from '@design-system/index';
import { AddDashed, PoubelleLine, PoubelleFull, DupliquerLine, DupliquerFull, NewsLine, RoundedArrow } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { NewsTable } from '@services/product/news/news.types';
import { ColumnDef } from '@tanstack/react-table';
import { useGetAllNewsMutation } from '@services/product/news/news.endpoints';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { useNewsListPageVM } from './useNewsListPageVM';
import moment from 'moment';
import { ImageLazy } from '@components/ImageLazy/ImageLazy';

export default function NewsListPage() {
    const {
        totalNews,
        handleMetadata,
        columnHelper,
        needReload,
        navigate,
        handleDeleteNews,
        handleDuplicateNews,
        handleDeleteMultipleNews,
        isLoading
    } = useNewsListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('coverImage', {
            header: 'Image',
            enableSorting: false,
            size: 52,
            meta: {
                clickable: true
            },
            cell: (info) => <ImageLazy height={52} width={52} src={info.getValue()?.viewLink ?? ''} />
        }),
        columnHelper.accessor('title', {
            header: 'Titre',
            id: 'title',
            enableSorting: true,
            minSize: 250,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('newsCategory', {
            header: 'Catégorie',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue().name}</>
        }),
        columnHelper.accessor('createdAt', {
            header: 'Date de création',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')}</>
        }),
        columnHelper.accessor('publicLink', {
            header: 'Voir en ligne',
            meta: {
                clickable: false,
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {info.row.original?.publicLink == null ? (
                        <>-</>
                    ) : (
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => {
                                window.open(info.getValue(), '_blank');
                            }}
                        />
                    )}
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            size: 0,
            header: 'Actions',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<DupliquerFull />} size={20} color="primary" />}
                            icon={<Icon icon={<DupliquerLine />} size={20} color="primary" />}
                            onClick={() => handleDuplicateNews(info.row.original.id)}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<PoubelleFull />} size={20} color="primary" />}
                            icon={<Icon icon={<PoubelleLine />} size={20} color="primary" />}
                            onClick={() => handleDeleteNews(info.row.original.id)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<NewsTable>[];

    return (
        <div className="products-table-container">
            <div className="products-table-container-header">
                <div className="products-table-container-header-left">
                    <SimpleNumberCard labelForSingle={'Actualité'} labelForMultiple={'Actualités'} value={totalNews} />
                    <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouvelle actualité"
                        onClick={() => navigate('/products-management/news/add')}
                    />
                </div>
            </div>
            <MutationTable
                key={'active'}
                needReload={needReload}
                columns={columns}
                mutationHook={useGetAllNewsMutation}
                onRowClick={(row) => navigate(`/products-management/news/${row.id}/edit`)}
                defaultSorting={{
                    field: 'id',
                    direction: 'desc'
                }}
                forceLoading={isLoading}
                headerTitle={'Liste des actualités'}
                headerIcon={<NewsLine />}
                onMetadata={handleMetadata}
                footer={{
                    actions: [
                        {
                            label: 'Supprimer',
                            icon: <Icon icon={<PoubelleLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={<PoubelleFull />} size={20} color="primary" />,
                            onClick: (lines: NewsTable[]) => handleDeleteMultipleNews(lines)
                        }
                    ]
                }}
            />
        </div>
    );
}
